<template>
    <div>
        <div class="main-heading">
            <h3>Select Your Location</h3>
            <div v-if="searchEnabled && shouldShowSearch" class="search">
                <input
                    id="search"
                    :placeholder="placeholderText"
                    v-model="searchTerm"
                    type="text"
                    class="form-control v2-styles"
                >
            </div>
        </div>
        <div class="main-content">
            <template v-if="searchByLocation && !foundWithinRadius">
                <div class="alert alert-secondary">Unfortunately, we don't have any locations within 4 miles of your search criteria</div>
                <h4>Nearest Locations</h4>
            </template>
            <div v-fix-height="{ el: ['h4', '.space-single-details'] }" ref="listingBlock" class="parking-listing">
                <space-single
                    v-for="(space, index) in filteredSpaces"
                    :key="index"
                    :instance="space.instance_id"
                    :id="space.id"
                    :space-id="space.space_id"
                    :title="space.title || space.space.title"
                    :dummy="!!space.space.dummy"
                    :rental-options="space.rentalOptions"
                    :image="space.image"
                    :editable="editable"
                />
            </div>
        </div>
        <a v-if="searchByLocation && foundWithinRadius" :href="listingsUrl" class="btn btn-primary">View all spaces</a>
    </div>
</template>

<script>
    import SpaceSingle from './SpaceSingle';
    import { getViewData } from '../../modules/view-data';


    export default {
        name: 'whitelabel-locations',

        components: { SpaceSingle },

        props: {
            spaces: {
                type: Array,
                default() {
                    return [];
                },
            },

            searchEnabled: {
                type: [Boolean, Number],
                default: 0,
            },

            editable: {
                type: Boolean,
                default: false,
            },

            searchByLocation: {
                type: [Boolean, Number],
                default: false,
            },

            foundWithinRadius: {
                type: [Boolean, Number],
                default: true,
            },
        },

        data() {
            return {
                searchTerm: null,
            };
        },

        computed: {
            /**
             * Computed value for the filteredSpaces array.
             *
             * If searched by location then spaces are ordered by the distance from search location - so no change
             *
             * If there is no search term currently the filteredSpaces matches the original spaces array.
             *
             * If there is a search term filter the original spaces array to return only spaces including the search term and return that.
             *
             * @returns {Array}
             */
            filteredSpaces() {
                if (this.searchByLocation) {
                    return this.spaces;
                }

                if (!this.searchTerm) {
                    return this.sortFilteredSpaces(this.spaces);
                }

                return this.sortFilteredSpaces(this.spaces).filter((space) => {
                    return space.title.toLowerCase().includes(this.searchTerm.toLowerCase());
                });
            },

            /**
             * Computed value for the placeholder - on page results filter.
             *
             * @returns {String}
             */
            placeholderText() {
                return getViewData('whitelabel_search', false) ? 'Filter results...' : 'Search for a location...';
            },

            /**
             * Route for the listings.
             *
             * @returns {String}
             */
            listingsUrl() {
                return '/listings';
            },

            /**
             * Returns true only when we haven't done a search using the landing page.
             *
             * @returns {Boolean}
             */
            shouldShowSearch() {
                return !window.location.search;
            },
        },

        watch: {
            /**
             * Watch the filtered spaces array. When it's updated call the fixHeight event to make sure they don't end up different heights
             * If there's no search term that means we're back to the original array so sort through the spaces to return them in a sorted order
             */
            filteredSpaces() {
                $(this.$refs.listingBlock).trigger('fixHeight');
            },
        },

        mounted() {
            ['start_date', 'end_date', 'start_time', 'end_time', 'rental', 'interval_count'].forEach((item) => {
                window.localStorage.removeItem(`search-${ item }`);
            });
        },

        methods: {
            /**
             * We have an issue that the title of the parking may not simply be 'Sherwood Street', it's possible it could be 'Parking at Sherwood Street'
             * This causes a bug if there are different starts to a space title (e.g. 'Parking at', 'Parking on', 'Parking near' - all 'Parking at' spaces will be listed prior to the 'Parking on' spaces regardless of the name of the space)
             *
             * In order to accommodate this and make sure spaces are returned in alphabetical order we need to compare and sort as we would have to anyway, but strip away the parts of the string that shouldn't be used
             *
             * @param {Array} spaces
             * @returns {Array} - ordered spaces array
             */
            sortFilteredSpaces(spaces) {
                /**
                 * In order to sort we must run through this compare function
                 * We're ordering the titles in alphabetical order so we take the title, change it to lower case so we don't confuse the regex;
                 *
                 * Regex: /parking\s\w*\s/
                 * So check for the word parking, a space, and any word following on from parking and remove it from the title.
                 * Then we sort against that alphabetically.
                 *
                 * @param {object} a
                 * @param {object} b
                 * @return {number} - the space's new position within array
                 */
                const compare = (a, b) => {
                    if (a.title.toLowerCase().replace(/parking\s\w*\s/g, '') <
                        b.title.toLowerCase().replace(/parking\s\w*\s/g, '')) {
                        return -1;
                    }

                    return 1;
                };

                return spaces.sort(compare);
            },
        },
    };
</script>

<style lang="less" scoped rel="stylesheet/less">
    .alert-secondary {
        background-color: #eeeeee;
        text-align: center;
        padding: 25px;
    }
</style>
